/**
 * ImpersonationTokenContext (US-AE11.F3-0039-S03 · @UC9102) — GIỮ access token "xem như user" trong
 * BỘ NHỚ (React state ở composition root), KHÔNG localStorage (token nhạy cảm, tránh persist/rò XSS).
 *
 * Vòng đời: start phiên → set token → panel read-only fetch màn hình user BẰNG token này (Bearer). Thoát
 * phiên / hết hạn → clear token (panel biến mất, không thể đọc tiếp). Nhờ vậy "View as user" HOẠT ĐỘNG
 * thật (không chỉ banner) — admin thấy đúng dữ liệu user qua ít nhất 1 GET user-scoped, chỉ đọc.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

interface ImpersonationTokenValue {
  /** Token impersonation hiện tại (null = không trong phiên). Panel dùng làm Bearer. */
  readonly token: string | null;
  /** Lưu token khi start phiên. */
  readonly setToken: (token: string) => void;
  /** Xoá token khi thoát/hết hạn (panel ẩn, không đọc tiếp được). */
  readonly clearToken: () => void;
}

const ImpersonationTokenContext = createContext<ImpersonationTokenValue | null>(null);

export function ImpersonationTokenProvider({ children }: { readonly children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const value = useMemo<ImpersonationTokenValue>(
    () => ({
      token,
      setToken: (t: string) => setTokenState(t),
      clearToken: () => setTokenState(null),
    }),
    [token],
  );
  return (
    <ImpersonationTokenContext.Provider value={value}>
      {children}
    </ImpersonationTokenContext.Provider>
  );
}

/**
 * Đọc context token. Thiếu Provider = lỗi lập trình (composition root quên bọc) → throw rõ ràng thay vì
 * fallback im lặng khiến "View as user" âm thầm không hoạt động.
 */
export function useImpersonationToken(): ImpersonationTokenValue {
  const ctx = useContext(ImpersonationTokenContext);
  if (!ctx) {
    throw new Error("useImpersonationToken phải nằm trong <ImpersonationTokenProvider>.");
  }
  return ctx;
}
